import { useMemo, useState } from "react";
import { searchChannels } from "@/lib/api";
import { toast } from "sonner";
import SaveNicheButton from "@/components/SaveNicheButton";

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
}

function fmtMoney(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return "$" + (n / 1_000).toFixed(1) + "K";
  return "$" + Math.round(n).toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function fmtDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function FormatPill({ fmt }) {
  if (!fmt) return <span className="text-zinc-600">—</span>;
  const styles = {
    shorts: "border-pink-500/40 text-pink-300 bg-pink-500/10",
    long: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    mixed: "border-yellow-500/40 text-yellow-300 bg-yellow-500/10",
  };
  return (
    <span
      data-testid={`format-pill-${fmt}`}
      title={
        fmt === "shorts" ? "Median upload ≤ 60s — Shorts-first channel"
        : fmt === "long" ? "Median upload > 60s — long-form channel"
        : "Mix of Shorts and long-form"
      }
      className={`inline-block font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[2px] rounded-sm border ${styles[fmt]}`}
    >
      {fmt}
    </span>
  );
}

const SUGGESTIONS = [
  "stoic philosophy",
  "self hosted ai",
  "lost history",
  "senior dog care",
  "single burner cooking",
  "zone 2 cardio",
  "ai coding tutorial",
  "indie game devlog",
];

// How many rows we show per page client-side. Backend always returns up
// to 50 in one call — we paginate locally to avoid burning extra
// YouTube API quota on every page-flip.
const PAGE_SIZE = 10;

export default function ChannelSearch() {
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("niche");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const runSearch = async (q, typeOverride) => {
    const v = (q ?? query).trim();
    if (!v) return;
    setLoading(true);
    setPage(1);
    try {
      const data = await searchChannels(v, 50, typeOverride ?? searchType);
      setResults(data);
      if (data.count === 0) {
        toast.info("No channels found — try a broader query");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Search failed";
      toast.error(typeof msg === "string" ? msg : "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch();
  };

  return (
    <div data-testid="channel-search-page" className="px-6 lg:px-10 py-8 max-w-[1500px] mx-auto">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // CHANNEL RECON
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-none mb-3">
        Look up any topic. See who&apos;s winning.
      </h1>
      <p className="text-zinc-400 text-base max-w-2xl mb-8 leading-relaxed">
        Search YouTube by niche topic OR by exact channel name. Surfaces up
        to 50 channels with subscribers, total views, video output, channel
        age, last upload, and a rough monthly-revenue estimate.
      </p>

      {/* Search-type toggle — sits ABOVE the search box so the user picks
          their intent before typing. Niche search ranks by est-revenue
          (who's making the most money in the space). Channel-name search
          biases toward name matches first. */}
      <div className="flex items-center gap-2 mb-3" data-testid="channel-search-type-toggle">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-1">
          Search by ▸
        </span>
        {[
          { key: "niche", label: "Niche topic", sub: "by subject" },
          { key: "channel", label: "Channel name", sub: "by exact name" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            data-testid={`channel-search-type-${opt.key}`}
            data-active={searchType === opt.key ? "true" : "false"}
            onClick={() => {
              setSearchType(opt.key);
              // If user already had results, re-run automatically.
              if (results && query.trim()) runSearch(query, opt.key);
            }}
            title={opt.sub}
            className={
              "font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border transition-colors " +
              (searchType === opt.key
                ? "border-yellow-400 text-yellow-400 bg-yellow-400/10"
                : "border-zinc-800 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400")
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <input
            data-testid="channel-search-input"
            type="text"
            placeholder={
              searchType === "channel"
                ? "e.g. Bright Side, MrBeast, Veritasium…"
                : "e.g. stoic philosophy, faceless finance, senior dog care…"
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-black border border-zinc-800 rounded-sm pl-4 pr-10 py-3 text-white text-sm focus:outline-none focus:border-yellow-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-yellow-400 text-lg leading-none px-1"
              title="Clear"
            >
              ×
            </button>
          )}
        </div>
        <button
          type="submit"
          data-testid="channel-search-submit"
          disabled={loading || !query.trim()}
          className="font-mono text-sm uppercase tracking-[0.2em] font-bold bg-yellow-400 text-black px-6 py-3 rounded-sm hover:bg-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? "Scanning…" : "▸ Search"}
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mb-10">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mr-1 self-center">
          try
        </span>
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            data-testid={`suggestion-${s.replace(/\s+/g, "-")}`}
            onClick={() => { setQuery(s); runSearch(s); }}
            className="font-mono text-xs uppercase tracking-wider px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400 rounded-sm transition-colors"
          >
            {s}
          </button>
        ))}
      </div>

      {loading && (
        <div className="font-mono text-sm text-zinc-500">
          [recon] sweeping channels<span className="blink">_</span>
        </div>
      )}

      {!loading && !results && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center bg-zinc-950">
          <div className="font-mono text-yellow-400 text-xs uppercase tracking-[0.3em] mb-4">
            [ AWAITING QUERY ]
          </div>
          <p className="text-zinc-400 text-sm">
            Type any topic above to surface the top channels working in that space.
          </p>
        </div>
      )}

      {!loading && results && (
        <ResultsTable results={results} page={page} setPage={setPage} />
      )}
    </div>
  );
}

function ResultsTable({ results, page, setPage }) {
  // Client-side sort. Backend returns channels in revenue-desc /
  // relevance order by default; this lets the user re-slice the list
  // by niche label, subs, views, or channel name without a round
  // trip. Selection is per-page-view (not persisted) so the next
  // search starts back at the default order.
  const [sortKey, setSortKey] = useState("revenue");
  const sorted = useMemo(() => {
    const arr = [...results.channels];
    switch (sortKey) {
      case "niche":
        // A→Z by niche label; missing niches sink to the bottom, and
        // within a niche we preserve the original (revenue-desc)
        // order so the top earners in each niche stay on top.
        return arr.sort((a, b) => {
          const na = (a.niche_label || "").trim();
          const nb = (b.niche_label || "").trim();
          if (!na && !nb) return 0;
          if (!na) return 1;
          if (!nb) return -1;
          return na.localeCompare(nb, undefined, { sensitivity: "base" });
        });
      case "subs":
        return arr.sort((a, b) => (b.subscribers || 0) - (a.subscribers || 0));
      case "views":
        return arr.sort((a, b) => (b.total_views || 0) - (a.total_views || 0));
      case "name":
        return arr.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
        );
      case "revenue":
      default:
        return arr; // preserve backend order
    }
  }, [results.channels, sortKey]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const visible = useMemo(
    () => sorted.slice(pageStart, pageEnd),
    [sorted, pageStart, pageEnd],
  );

  if (results.count === 0) {
    return (
      <div className="font-mono text-sm text-zinc-500">
        No channels matched “{results.query}”.
      </div>
    );
  }

  const totals = results.channels.reduce(
    (acc, c) => ({
      subs: acc.subs + (c.subscribers || 0),
      views: acc.views + (c.total_views || 0),
      videos: acc.videos + (c.video_count || 0),
      rev: acc.rev + (c.est_monthly_revenue || 0),
    }),
    { subs: 0, views: 0, videos: 0, rev: 0 },
  );

  return (
    <div data-testid="channel-results">
      {/* Aggregate strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-6 rounded-sm overflow-hidden">
        <Stat label={`Channels found · ${results.search_type === "channel" ? "name match" : "niche"}`} value={results.count} />
        <Stat label="Combined subs" value={fmtNum(totals.subs)} />
        <Stat label="Combined views" value={fmtNum(totals.views)} />
        <Stat label="Est. combined $/mo" value={fmtMoney(totals.rev)} />
      </div>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="font-mono text-[11px] text-zinc-500">
          // Showing {pageStart + 1}-{Math.min(pageEnd, sorted.length)} of {sorted.length} ·{" "}
          {sortKey === "revenue"
            ? "sorted by est. monthly revenue (niche-aware RPM)"
            : sortKey === "niche"
              ? "sorted by niche label (A→Z)"
              : sortKey === "subs"
                ? "sorted by subscribers (high→low)"
                : sortKey === "views"
                  ? "sorted by lifetime views (high→low)"
                  : "sorted by channel name (A→Z)"}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            sort
            <select
              data-testid="channel-search-sort-select"
              value={sortKey}
              onChange={(e) => {
                setSortKey(e.target.value);
                setPage(1);
              }}
              className="bg-black border border-zinc-800 hover:border-yellow-400/60 focus:border-yellow-400 text-xs text-zinc-100 font-mono px-2 py-1.5 rounded-sm focus:outline-none transition-colors"
            >
              <option value="revenue">Est. revenue</option>
              <option value="niche">Niche (A→Z)</option>
              <option value="subs">Subscribers</option>
              <option value="views">Lifetime views</option>
              <option value="name">Channel name</option>
            </select>
          </label>
          {pageCount > 1 && (
            <Pagination page={safePage} pageCount={pageCount} setPage={setPage} />
          )}
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <div className="grid grid-cols-16 px-4 py-3 bg-zinc-950 border-b border-zinc-800 font-mono text-[11px] tracking-normal text-zinc-400">
          <div className="col-span-4">Channel</div>
          <div className="col-span-2 text-right">Est. $/mo</div>
          <div className="col-span-1 text-right">Subs</div>
          <div className="col-span-2 text-right">Lifetime views</div>
          <div className="col-span-1 text-right">Posts/mo</div>
          <div className="col-span-1 text-right">Avg len</div>
          <div className="col-span-1 text-right">Format</div>
          <div className="col-span-1 text-right">Videos</div>
          <div className="col-span-1 text-right">Created</div>
          <div className="col-span-1 text-right">Last post</div>
          <div className="col-span-1 text-right">Save</div>
        </div>
        {visible.map((c) => (
          <div
            key={c.channel_id}
            data-testid={`channel-row-${c.channel_id}`}
            className="grid grid-cols-16 px-4 py-3 border-b border-zinc-900 last:border-b-0 items-center hover:bg-zinc-950 transition-colors"
          >
            <a
              href={c.url}
              target="_blank"
              rel="noreferrer"
              className="col-span-4 flex items-center gap-3 min-w-0"
            >
              {c.thumbnail ? (
                <img
                  src={c.thumbnail}
                  alt=""
                  className="h-10 w-10 rounded-full border border-zinc-800 shrink-0 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-zinc-800 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-sm text-white font-semibold truncate">
                  {c.name}
                </div>
                <div className="text-[11px] text-zinc-500 font-mono truncate">
                  {c.description || "—"}
                </div>
              </div>
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-2 text-right font-mono font-bold text-yellow-400">
              <div>{fmtMoney(c.est_monthly_revenue)}</div>
              {c.rpm_used != null && (
                <div
                  title={`Niche-aware RPM: $${c.rpm_used.toFixed(2)} (${c.niche_label || "General"})`}
                  className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5 font-normal"
                >
                  ${Number(c.rpm_used).toFixed(2)} RPM · {c.niche_label || "General"}
                </div>
              )}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right font-mono text-sm text-zinc-200">
              {c.subscribers_hidden ? "hidden" : fmtNum(c.subscribers)}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-2 text-right font-mono text-sm text-zinc-200">
              {fmtNum(c.total_views)}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right font-mono text-[11px] text-zinc-300">
              {c.posts_per_month ? c.posts_per_month.toFixed(1) : "—"}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right font-mono text-[11px] text-zinc-300">
              {fmtDuration(c.median_duration_sec)}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right">
              <FormatPill fmt={c.format} />
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right font-mono text-sm text-zinc-200">
              {fmtNum(c.video_count)}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right font-mono text-[11px] text-zinc-400">
              {fmtDate(c.channel_created)}
            </a>
            <a href={c.url} target="_blank" rel="noreferrer" className="col-span-1 text-right font-mono text-[11px] text-zinc-400">
              {fmtDate(c.last_upload)}
            </a>
            <div className="col-span-1 flex justify-end">
              <SaveNicheButton channel={c} source="channel_search" />
            </div>
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="mt-4 flex justify-end">
          <Pagination page={safePage} pageCount={pageCount} setPage={setPage} />
        </div>
      )}

      <div className="font-mono text-[11px] text-zinc-600 mt-4 leading-relaxed">
        Revenue estimate uses each channel&apos;s <em>recent</em> upload performance —
        median views per video × posts/mo × a niche-aware RPM (Finance/Tech
        pull $10-15, Education/News $6-7, Gaming/Kids/Music under $3). Falls
        back to lifetime average + $5 RPM if YouTube hides recent uploads.
        Treat as order-of-magnitude — real RPM swings by geo, season, and ad fill.
      </div>
    </div>
  );
}

function Pagination({ page, pageCount, setPage }) {
  // Compact pager — prev/next + numeric page indicator. We keep it
  // simple instead of rendering all page buttons because at 50 results
  // / page 10 that's only 5 pages max — clutter-free.
  return (
    <div className="flex items-center gap-1" data-testid="channel-search-pagination">
      <button
        type="button"
        data-testid="channel-search-page-prev"
        disabled={page <= 1}
        onClick={() => setPage(page - 1)}
        className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-zinc-800 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        ‹ prev
      </button>
      <span
        data-testid="channel-search-page-indicator"
        className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-2"
      >
        Page {page} / {pageCount}
      </span>
      <button
        type="button"
        data-testid="channel-search-page-next"
        disabled={page >= pageCount}
        onClick={() => setPage(page + 1)}
        className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-zinc-800 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        next ›
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-black p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className="font-mono font-bold text-2xl text-yellow-400 tracking-tight">
        {value}
      </div>
    </div>
  );
}
